/**
 * Title and one-line purpose for every route, shown in the topbar.
 *
 * V20 kept the same table and it does real work: the heading tells you where
 * you are, and the line under it tells you what the screen is for without
 * having to click anything. Ported wording where the screen is the same.
 */
export const PAGE_META: Record<string, [string, string]> = {
  '/': ['Dashboard', 'Your holiday, tasks, messages and employment information'],
  '/leave': ['My holiday & leave', 'View balances, requests and book time away'],
  '/calendar': ['Company calendar', 'Privacy-limited availability by day, week and month'],
  '/sickness': ['Sickness & absence', 'Absence records, evidence and return-to-work'],
  '/contract': ['My contracts', 'Signed contracts, variations and appointment documents'],
  '/handbook': ['Employee handbook', 'The current People Group Services employment handbook'],
  '/policies': ['Company policies', 'Review the policies incorporated into your employment framework'],
  '/documents': ['Payslips & documents', 'Your private payroll, tax, pension and employment documents'],
  '/new-starter': ['Employment pack', 'Contract, handbook, policies and required acknowledgements'],
  '/messages': ['Messages', 'Speak directly with your line manager, HR or learning support'],
  '/notifications': ['Notifications', 'Portal alerts raised for you'],
  '/benefits': ['Benefits package', 'Active benefits, eligibility and available schemes'],
  '/training': ['Learning & CPD', 'Assigned courses, course catalogue and certificate records'],
  '/directory': ['Telephone directory', 'Colleagues who have consented to share their contact details'],
  '/profile': ['My details', 'Review your employment record and request changes'],
  '/help': ['Help & support', 'Answers, contacts and guidance for using HR Portal'],
  '/change-password': ['Security & password', 'Change your password and review account security'],

  '/approvals': ['Approvals', 'Holiday, leave, benefit and employee-change decisions'],
  '/people': ['Team people', 'Operational records for your permitted employees'],
  '/analytics': ['Absence & cost', 'Absence patterns, coverage risk and holiday liability'],

  '/admin/people': ['Employee records', 'Create, amend and administer the employee database'],
  '/admin/organisation': ['Departments & roles', 'Organisation structure, departments and job titles'],
  '/admin/payroll': ['Payroll documents', 'Bulk payslip and P60 publication'],
  '/admin/documents': ['Document control', 'Word uploads, versions, assignment and acknowledgements'],
  '/admin/policies': ['Policies', 'Publish policy versions and track acknowledgement'],
  '/admin/contracts': ['Contracts', 'Issue contracts and record signature'],
  '/admin/benefits': ['Benefit schemes', 'Scheme configuration and employee requests'],
  '/admin/training': ['Course catalogue', 'Courses, assignment and certificate verification'],
  '/admin/data': ['Employee CSV', 'Import, export and map employee database information'],
  '/admin/accounts': ['Users & invitations', 'Create access accounts, invite employees and manage permissions'],
  '/admin/settings': ['Portal controls', 'Notifications, delegation and system controls'],
};

export interface Section {
  label: string;
  roleLabel: string;
  chipClass: string;
}

/**
 * Which of the three sections the viewer is in.
 *
 * V20 shipped these as three separate sign-ins into one program. Here it is
 * one application and the section is derived from capability, so the label
 * follows what the server already lets this person do rather than what they
 * picked from a dropdown before authenticating.
 */
export function sectionFor(isHr: boolean, isManager: boolean): Section {
  if (isHr) return { label: 'HR SECTION', roleLabel: 'HR administration', chipClass: 'hr' };
  if (isManager) {
    return {
      label: 'MANAGEMENT SECTION',
      roleLabel: 'Line manager / senior manager',
      chipClass: 'management',
    };
  }
  return { label: 'EMPLOYEE SECTION', roleLabel: 'Employee', chipClass: '' };
}
